import { formatCompactCurrency, formatCount, formatPercent } from '../../lib/format.js';
import type { CreativeRow } from '../creatives/data/api.js';
import { asRecord, num, text } from './resultValues.js';

export const SPEND_METRIC = 'Spend';
const FATIGUE = 'fatigue';
const FATIGUE_RATIO: Record<string, { path: string; improvesAbove: boolean }> = {
  CTR: { path: 'ctr_ratio', improvesAbove: true },
  CPM: { path: 'cpm_ratio', improvesAbove: false },
};

export const ROWS_BY_KIND: Record<string, string> = {
  creative_list: 'creatives',
  comparison: 'creatives',
};

export interface ResultRow {
  id: string;
  label: string;
  // The service's own id for the creative, absent on a row that carries none.
  // `id` is this row's place in the result and `label` falls back to it, so
  // neither can key anything the service has to read back.
  creativeId: string | null;
  values: Record<string, unknown>;
  thumbnailUrl: string | null;
  previewUrl: string | null;
  network: string | null;
  daysLive: number | null;
  duration: number | null;
  type: string | null;
}

export interface Metric {
  metric: string;
  value: number | null;
  format: string;
  precision: number | null;
  currencySymbol: string | null;
  isImprovement: boolean | null;
  changePercent: number | null;
  changeLabel: string | null;
  previousValue: number | null;
}

export interface TableColumn {
  label: string;
  name: string;
}

export interface MetricDelta {
  text: string;
  isImprovement: boolean;
  label: string | null;
}

export function metricsOf(row: Record<string, unknown>): Metric[] {
  const metrics = row['metrics'];
  if (!Array.isArray(metrics)) return [];
  return metrics.flatMap((entry) => {
    const metric = asRecord(entry);
    const name = metric === null ? null : text(metric['metric']);
    if (metric === null || name === null) return [];
    return [
      {
        metric: name,
        value: num(metric['value']),
        format: text(metric['format']) ?? 'count',
        precision: num(metric['precision']),
        currencySymbol: text(metric['currency_symbol']),
        isImprovement:
          typeof metric['is_improvement'] === 'boolean' ? metric['is_improvement'] : null,
        changePercent: num(metric['change_percent']),
        changeLabel: text(metric['change_label']),
        previousValue: num(metric['previous_value']),
      },
    ];
  });
}

export function formatValue(
  value: unknown,
  format: string,
  currencySymbol = '$',
  precision: number | null = null,
): string {
  const value_ = num(value);
  if (value_ === null) return '—';
  if (format === 'percentage') return formatPercent(value_, precision ?? 1);
  if (format === 'compact_currency') return formatCompactCurrency(value_, currencySymbol);
  return formatCount(value_);
}

function asset(row: Record<string, unknown>): string | null {
  return text(row['asset_url']);
}

function isImage(row: Record<string, unknown>): boolean {
  return (text(row['type']) ?? '').toUpperCase() === 'IMAGE';
}

export function rowsOf(result: unknown, resultKind: string | null): ResultRow[] {
  const holder = asRecord(result);
  const key = resultKind === null ? undefined : ROWS_BY_KIND[resultKind];
  if (holder === null || key === undefined) return [];
  const rows = holder[key];
  if (!Array.isArray(rows)) return [];

  return rows.flatMap((entry, index) => {
    const row = asRecord(entry);
    if (row === null) return [];
    return [
      {
        id: String(index),
        label: text(row['creative_id']) ?? String(index),
        creativeId: text(row['creative_id']),
        values: row,
        // An image row's asset is a still, not something a <video> can play, and
        // handing it to one renders nothing *and* hides the broken-media icon.
        thumbnailUrl: text(row['thumbnail_url']) ?? (isImage(row) ? asset(row) : null),
        previewUrl: text(row['preview_url']) ?? (isImage(row) ? null : asset(row)),
        network: text(row['network']),
        daysLive: num(row['days_live']),
        duration: num(row['duration']),
        type: text(row['type']),
      },
    ];
  });
}

export function columnsOf(rows: ResultRow[]): TableColumn[] {
  const seen = new Set<string>();
  const columns: TableColumn[] = [];
  for (const row of rows) {
    for (const metric of metricsOf(row.values)) {
      if (metric.metric === SPEND_METRIC || seen.has(metric.metric)) continue;
      seen.add(metric.metric);
      columns.push({ label: metric.metric, name: metric.metric });
    }
  }
  return columns;
}

export function currencyOf(row: Record<string, unknown>): string {
  for (const metric of metricsOf(row)) {
    if (metric.currencySymbol !== null) return metric.currencySymbol;
  }
  return '$';
}

function ratioDelta(row: ResultRow, name: string): MetricDelta | null {
  const rule = FATIGUE_RATIO[name];
  const block = asRecord(row.values[FATIGUE]);
  if (rule === undefined || block === null) return null;
  const ratio = num(block[rule.path]);
  if (ratio === null) return null;
  const change = ratio - 1;
  return {
    text: `${change > 0 ? '↑' : '↓'} ${formatPercent(Math.abs(change), 0)}`,
    isImprovement: rule.improvesAbove ? change > 0 : change < 0,
    label: 'vs. Prev. Period',
  };
}

export function metricDeltaOf(metric: Metric): MetricDelta | null {
  if (
    metric.previousValue === null ||
    metric.changePercent === null ||
    metric.isImprovement === null
  )
    return null;
  return {
    text: `${metric.changePercent > 0 ? '↑' : '↓'} ${Math.abs(metric.changePercent).toFixed(0)}%`,
    isImprovement: metric.isImprovement,
    label: metric.changeLabel,
  };
}

export function deltaOf(row: ResultRow, column: TableColumn): MetricDelta | null {
  const metric = metricsOf(row.values).find((entry) => entry.metric === column.name);
  if (metric === undefined) return null;
  return metricDeltaOf(metric) ?? ratioDelta(row, column.name);
}

export function metricValueOf(row: ResultRow, name: string): number | null {
  return metricsOf(row.values).find((metric) => metric.metric === name)?.value ?? null;
}

export function cellOf(row: ResultRow, column: TableColumn): string {
  const metric = metricsOf(row.values).find((entry) => entry.metric === column.name);
  if (metric === undefined) return '—';
  return formatValue(metric.value, metric.format, metric.currencySymbol ?? '$', metric.precision);
}

export function summaryOf(result: unknown): string | null {
  const holder = asRecord(result);
  if (holder === null) return null;
  const summary = holder['summary'];
  return typeof summary === 'string' && summary !== '' ? summary : null;
}

export function spendOf(row: ResultRow): string | null {
  const spend = metricsOf(row.values).find((metric) => metric.metric === SPEND_METRIC);
  if (spend === undefined) return null;
  return formatValue(spend.value, spend.format, spend.currencySymbol ?? '$', spend.precision);
}

export function improvementOf(row: ResultRow, column: TableColumn): boolean | null {
  const metric = metricsOf(row.values).find((entry) => entry.metric === column.name);
  return metric?.isImprovement ?? null;
}

export function creativeRowsOf(rows: ResultRow[]): CreativeRow[] {
  return rows.flatMap((row, index) =>
    row.creativeId === null ||
    rows.findIndex((other) => other.creativeId === row.creativeId) !== index
      ? []
      : [
          {
            creativeId: row.creativeId,
            thumbnailUrl: row.thumbnailUrl ?? '',
            previewUrl: row.previewUrl ?? '',
            duration: row.duration,
            format: null,
            network: row.network,
            creativeType: row.type,
            spendRatio: null,
            activeDays: row.daysLive ?? 0,
            firstSeen: null,
            lastSeen: null,
            metrics: {},
            uplift: {},
          },
        ],
  );
}
